import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useAuth } from "@/providers";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { Goto } from "@/shared/navigation/Goto";

import { AISettings } from "./AISettings";
import { CustomFields } from "./CustomFields";
import { Integrations } from "./Integrations";
import { OAuthCallbackHandler } from "./McpConnections";
import { OrganizationSettings } from "./OrganizationSettings";
import { Secrets } from "./Secrets";
import { Tokens } from "./Tokens";
import { Usage } from "./Usage";

/**
 * Forwards a Slack OAuth redirect that landed on the callback's old Settings path, which an API
 * deployed before the move still hands to Slack. The query string carries the single-use
 * authorization code, so it has to survive the hop — a bare `<Navigate to>` drops it and strands
 * an install the admin already approved, with the popup sitting on a page that reports nothing
 * back to the opener.
 */
function LegacySlackCallbackRedirect() {
  const { search } = useLocation();

  return (
    <Navigate to={`${Goto.route.SlackIntegrationCallback}${search}`} replace />
  );
}

export function SettingsTabRoutes() {
  const { currentOrganization, currentUser } = useAuth();
  const customFieldsEnabled = useCheckFeatureFlag("release-custom-fields");

  return (
    <Routes>
      <Route
        path="organization"
        element={
          <OrganizationSettings
            currentOrg={currentOrganization}
            currentUser={currentUser}
          />
        }
      />
      <Route path="tokens" element={<Tokens />} />
      <Route path="usage" element={<Usage showGraphs={true} />} />
      <Route path="ai" element={<AISettings />} />
      <Route path="ai/mcp-callback" element={<OAuthCallbackHandler />} />
      {/* Skills moved to the Agents page; redirect the old Settings URL. */}
      <Route path="skills" element={<Navigate to="/agents/skills" replace />} />
      <Route path="secrets" element={<Secrets />} />
      <Route path="integrations" element={<Integrations />} />
      {/* Slack moved to its own top-level page; redirect the old Settings URLs. */}
      <Route
        path="integrations/slack"
        element={<Navigate to={Goto.route.SlackIntegration} replace />}
      />
      <Route
        path="integrations/slack/callback"
        element={<LegacySlackCallbackRedirect />}
      />

      {customFieldsEnabled && (
        <Route path="custom-fields" element={<CustomFields />} />
      )}
      {/* Redirect old MCP routes to AI tab */}
      <Route
        path="integrations/register-mcp-server"
        element={<Navigate to="/settings/ai" replace />}
      />
      <Route
        path="mcp-connections"
        element={<Navigate to="/settings/ai" replace />}
      />
      <Route
        path="mcp-connections/callback"
        element={<OAuthCallbackHandler />}
      />
      <Route
        path="*"
        element={<Navigate to="/settings/organization" replace />}
      />
    </Routes>
  );
}
